import React, { useEffect, useState } from "react";
import Valide from '../assets/images/valide.svg';
import { useNavigate } from "react-router-dom";
import MenuHeader from "./MenuHeader";
import { useTranslation } from 'react-i18next';

import {
    Button,
    Container,
    createTheme,
    ThemeProvider,
    Menu,
    MenuItem,
    ListItemIcon,
    Typography,
    Paper
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Event as EventIcon,
    Google,
    Apple,
    Microsoft
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import BookingService from "../services/booking.service";
import PromotionService from "../services/promotion.service";
import AuthService from "../services/auth.service";
import useApi from "../hooks/useApi";
import useAuth from "../hooks/useAuth";
import { createEventDedupeKey, pushDataLayerEvent } from "../utils/gtmDataLayer";
import {
    buildEcommerceObject,
    buildItemsFromBooking,
    buildUserDataObject,
    calculateValueFromItems,
    computeTaxFromTtc,
    extractPaymentMethodFromTransactions,
    resolveBookingTransactionId,
} from "../utils/gtmEcommerce";
import { buildGtmEventFields } from "../utils/gtmEventFields";
import { getUserDisplayName } from "../utils/userDisplay";

const PENDING_GIFT_TRACKING_KEY = "pendingGiftTracking";

const StyledMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: '12px',
        minWidth: '280px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        padding: theme.spacing(1),
    },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    borderRadius: '8px',
    padding: '12px 16px',
    margin: '4px 0',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const CalendarButton = styled(Button)({
    fontSize: '18px',
    textTransform: 'none',
    borderRadius: '15px',
    width: '100%',
    maxWidth: '315px',
    minHeight: '57px',
    gap: '8px',
    display: 'flex',
    alignItems: 'center',
});

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const getLocalizedValue = (value) => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed || undefined;
    }
    if (typeof value === "number") return String(value);
    if (typeof value === "object") {
        const candidate =
            value.fr ??
            value.en ??
            value.de ??
            value.it ??
            value.label ??
            value.title ??
            value.name;

        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }
    }
    return undefined;
};

const ConfirmationPage = () => {
    const api = useApi();
    const { t, i18n } = useTranslation('confirmation');
    const theme = createTheme({
        palette: {
            white: {
                "main": "#FFFFFF",
                "light": "#FFFFFF",
                "dark": "#FFFFFF",
                "contrastText": "#FFFFFF"
            },
            white_dark: {
                "main": "rgba(255, 255, 255, 0.7)",
                "light": "rgba(255, 255, 255, 0.7)",
                "dark": "rgba(255, 255, 255, 0.7)",
                "contrastText": "#393D44"
            },
            dark_yellow: {
                "main": "#393D44",
                "light": "#393D44",
                "dark": "#393D44",
                "contrastText": "#FAEF9B",
            },
            dark_white: {
                "main": "#393D44",
                "light": "#393D44",
                "dark": "#393D44",
                "contrastText": "#FFFFFF",
            },
            dark: {
                "main": "#393D44",
                "light": "#393D44",
                "dark": "#393D44",
                "contrastText": "#393D44"
            }
        },
    });
    const [success, setSuccess] = useState(false);
    const [isBooking, setIsBooking] = useState(false);
    const [isPromotion, setIsPromotion] = useState(false);
    const [promotionId, setPromotionId] = useState(null);
    const [bookingDetails, setBookingDetails] = useState(null);
    const [promotionDetails, setPromotionDetails] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const auth = useAuth();
    const [trackingUser, setTrackingUser] = useState(auth?.user || null);

    useEffect(() => {
        setTrackingUser(auth?.user || null);
    }, [auth?.user]);

    const hasPrimaryAddress = (user) =>
        Boolean(
            user?.primary_address?.id
            || user?.primary_address?.street
            || user?.primary_address?.zip_code
            || user?.primaryAddress?.id
            || user?.primaryAddress?.street
            || user?.primaryAddress?.zip_code
        );

    const resolveTrackingUser = async () => {
        if (!auth?.user?.id) {
            return trackingUser || auth?.user || null;
        }

        const currentUser = trackingUser || auth?.user;
        if (hasPrimaryAddress(currentUser)) {
            return currentUser;
        }

        try {
            const response = await AuthService.me(
                { id: auth.user.id, with: "primary_address" },
                api
            );
            const nextUser = response?.user || currentUser;
            setTrackingUser(nextUser || null);
            return nextUser || currentUser || null;
        } catch (_) {
            return currentUser || null;
        }
    };

    const extractBookingFromResponse = (response) => {
        if (Array.isArray(response)) return response[0] || null;
        if (Array.isArray(response?.data)) return response.data[0] || null;
        if (Array.isArray(response?.data?.data)) return response.data.data[0] || null;
        if (response?.id) return response;
        if (response?.data?.id) return response.data;
        return null;
    };

    const fetchBookingDetails = (bookingId) => {
        if (!bookingId) return Promise.resolve(null);
        return BookingService.index(
            {
                id: bookingId,
                with: "address,address.hotel,therapists,client,products.category,products.domain,hotel,transactions",
            },
            api
        )
            .then((response) => {
                const booking = extractBookingFromResponse(response);
                if (booking) {
                    setBookingDetails(booking);
                }
                return booking;
            })
            .catch(() => null);
    };

    const extractPromotionFromResponse = (response) => {
        if (response?.id) return response;
        if (response?.data?.id) return response.data;
        if (Array.isArray(response?.data) && response.data.length > 0) return response.data[0];
        return null;
    };

    const fetchPromotionDetails = (currentPromotionId) => {
        if (!currentPromotionId) return Promise.resolve(null);
        return PromotionService.get(
            currentPromotionId,
            { with: "product,address,transaction" },
            api
        )
            .then((response) => {
                const promotion = extractPromotionFromResponse(response);
                if (promotion) {
                    setPromotionDetails(promotion);
                }
                return promotion;
            })
            .catch(() => null);
    };

    useEffect(() => {
        const bookingId = new URLSearchParams(window.location.search).get("booking_id");
        const currentPromotionId = new URLSearchParams(window.location.search).get("promotion_id");
        sessionStorage.removeItem("pendingBookingUpdate");
        sessionStorage.removeItem("paymentData");
        sessionStorage.removeItem("editBookingMode");
        sessionStorage.removeItem("productChanged");
        sessionStorage.removeItem("therapistChanged");
        sessionStorage.removeItem("addressChanged");

        if (bookingId) {
            fetchBookingDetails(bookingId);
        }

        if (currentPromotionId) {
            fetchPromotionDetails(currentPromotionId);
        }
    }, []);


    useEffect(() => {
        checkPaymentType();
        checkSuccessParaminUrl();
    }, []);
    const checkSuccessParaminUrl = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('success')) {
            setSuccess(true);
        } else {
            setSuccess(false);
        }
        window.history.replaceState({}, document.title, window.location.pathname);
    };

    const checkPaymentType = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const bookingId = urlParams.get('booking_id');
        const currentPromotionId = urlParams.get('promotion_id');
        setPromotionId(currentPromotionId);

        if (bookingId) {
            setIsBooking(true);
        } else {
            setIsBooking(false);
        }

        if (currentPromotionId) {
            setIsPromotion(true);
        } else {
            setIsPromotion(false);
        }
    };

    useEffect(() => {
        if (!success || !isBooking || !bookingDetails?.id) return;

        let cancelled = false;
        const trackBookingEvents = async () => {
            try {
                const resolvedTrackingUser = await resolveTrackingUser();
                if (cancelled) return;

                const items = buildItemsFromBooking(bookingDetails, { booking: bookingDetails });
                if (!items.length) return;

                const fallbackValue = calculateValueFromItems(items);
                const totalFare = Number(bookingDetails?.total_fare);
                const subtotal = Number(bookingDetails?.subtotal);
                const orderValue = Number.isFinite(totalFare)
                    ? totalFare
                    : Number.isFinite(subtotal)
                        ? subtotal
                        : fallbackValue;
                const paymentMethod = extractPaymentMethodFromTransactions(bookingDetails?.transactions);
                const transactionId = resolveBookingTransactionId(bookingDetails);
                const bookingUserData = buildUserDataObject({
                    user: resolvedTrackingUser,
                    client: bookingDetails?.client,
                    booking: bookingDetails,
                    address: bookingDetails?.address,
                    phoneNumber: bookingDetails?.phone || resolvedTrackingUser?.phone || auth?.user?.phone,
                    emailAddress: bookingDetails?.client?.email || resolvedTrackingUser?.email || auth?.user?.email,
                    isConnectedUser: Boolean(auth?.user?.id),
                });

            pushDataLayerEvent(
                "add_payment_info",
                {
                    user_data: bookingUserData,
                    ...buildGtmEventFields({
                        language: i18n.language,
                        user: resolvedTrackingUser || auth?.user,
                        booking: bookingDetails,
                        flypros: bookingDetails?.therapists,
                    }),
                    ecommerce: buildEcommerceObject({
                        currency: "CHF",
                        value: orderValue,
                        coupon: bookingDetails?.promocode,
                        tax: computeTaxFromTtc(orderValue),
                        paymentType: paymentMethod,
                        items,
                    }),
                },
                {
                    dedupeKey: createEventDedupeKey("add_payment_info", transactionId),
                }
            );

            pushDataLayerEvent(
                "purchase",
                {
                    user_data: bookingUserData,
                    ...buildGtmEventFields({
                        language: i18n.language,
                        user: resolvedTrackingUser || auth?.user,
                        booking: bookingDetails,
                        flypros: bookingDetails?.therapists,
                    }),
                    ecommerce: buildEcommerceObject({
                        currency: "CHF",
                        transactionId,
                        value: orderValue,
                        coupon: bookingDetails?.promocode,
                        tax: computeTaxFromTtc(orderValue),
                        paymentType: paymentMethod,
                        items,
                    }),
                },
                {
                    dedupeKey: createEventDedupeKey("purchase", transactionId),
                }
            );
        } catch (trackingError) {
            console.error("Failed to push confirmation events:", trackingError);
        }
        };

        trackBookingEvents();

        return () => {
            cancelled = true;
        };
    }, [success, isBooking, bookingDetails, auth?.user, i18n.language]);

    useEffect(() => {
        if (!success || !isPromotion) return;

        let cancelled = false;
        const trackGiftEvents = async () => {
            let pendingGiftTracking = null;
            try {
                const raw = sessionStorage.getItem(PENDING_GIFT_TRACKING_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    const savedAt = Number(parsed?.savedAt);
                    const isFresh = Number.isFinite(savedAt) ? Date.now() - savedAt <= 6 * 60 * 60 * 1000 : true;
                    const parsedPromotionId = parsed?.promotionId;
                    const matchesPromotion = !promotionId
                        || (parsedPromotionId !== null && parsedPromotionId !== undefined && String(parsedPromotionId) === String(promotionId));

                    if (parsed && typeof parsed === "object" && isFresh && matchesPromotion) {
                        pendingGiftTracking = parsed;
                    }
                }
            } catch (_) {
                pendingGiftTracking = null;
            }

            const promotion = promotionDetails || null;
            if (!promotion && !pendingGiftTracking) return;

            try {
                const resolvedTrackingUser = await resolveTrackingUser();
                if (cancelled) return;

                const promotionTransactions = asArray(
                    promotion?.transaction ?? promotion?.transactions
                ).filter(Boolean);

                const primaryTransaction =
                    promotionTransactions.find((tx) =>
                        ["FULFILL", "COMPLETED", "AUTHORIZED"].includes(String(tx?.status || "").toUpperCase())
                    ) || promotionTransactions[0];

                const fallbackPrice = Number(primaryTransaction?.amount);
                const itemNameFromPromotion =
                    getLocalizedValue(promotion?.product?.name) ||
                    getLocalizedValue(promotion?.name) ||
                    "gift_offer";
                const promotionLabel = getLocalizedValue(promotion?.name) || "Bons Cadeaux & Packages";
                const inferredCategory = /hotel|h[oô]tel/i.test(promotionLabel || "")
                    ? "hotel_partenaire"
                    : "a_domicile";
                const fallbackItems = [
                    {
                        item_id: String(promotion?.product_id ?? promotion?.id ?? "gift_offer"),
                        item_name: itemNameFromPromotion,
                        price: Number.isFinite(fallbackPrice) ? fallbackPrice : 0,
                        quantity: 1,
                        discount: 0,
                        item_brand: "Flyspa",
                        item_category: inferredCategory,
                        item_category2:
                            getLocalizedValue(promotion?.product?.category?.title) ||
                            getLocalizedValue(promotion?.product?.category?.name),
                        item_list_id: "massage_services",
                        item_list_name: promotionLabel,
                        location_id:
                            promotion?.address?.place_id ??
                            promotion?.address?.id ??
                            promotion?.address_id ??
                            undefined,
                        index: 0,
                    },
                ];

                const items =
                    Array.isArray(pendingGiftTracking?.items) && pendingGiftTracking.items.length > 0
                        ? pendingGiftTracking.items
                        : fallbackItems;

                const fallbackValue = calculateValueFromItems(items);
                const orderValue = Number.isFinite(Number(pendingGiftTracking?.value))
                    ? Number(pendingGiftTracking.value)
                    : Number.isFinite(fallbackPrice)
                        ? fallbackPrice
                        : fallbackValue;
                const transactionId =
                    pendingGiftTracking?.transactionId ??
                    primaryTransaction?.transaction_id ??
                    promotion?.id;
                const paymentMethod =
                    pendingGiftTracking?.paymentType ||
                    extractPaymentMethodFromTransactions(promotionTransactions);
                const giftPromotionId = promotion?.id ?? promotionId ?? pendingGiftTracking?.promotionId;
                const currency = primaryTransaction?.currency || "CHF";
                const couponValue = pendingGiftTracking?.coupon ?? "";
                const pendingGiftUserData =
                    pendingGiftTracking?.userData && typeof pendingGiftTracking.userData === "object"
                        ? pendingGiftTracking.userData
                        : null;
                const pendingGiftHasAddress =
                    Boolean(pendingGiftUserData?.street)
                    || Boolean(pendingGiftUserData?.postal_code)
                    || Boolean(pendingGiftUserData?.city)
                    || Boolean(pendingGiftUserData?.region);
                const giftUserData = pendingGiftHasAddress
                    ? pendingGiftUserData
                    : buildUserDataObject({
                        user: resolvedTrackingUser,
                        promotion,
                        address: promotion?.address,
                        emailAddress:
                            promotion?.buyer_email
                            || promotion?.to_user_email
                            || resolvedTrackingUser?.email
                            || auth?.user?.email
                            || undefined,
                        userId: promotion?.client_id || resolvedTrackingUser?.id,
                        isConnectedUser: Boolean(auth?.user?.id),
                    });

            pushDataLayerEvent(
                "add_payment_info",
                {
                    user_data: giftUserData,
                    ...buildGtmEventFields({
                        language: i18n.language,
                        user: resolvedTrackingUser || auth?.user,
                    }),
                    ecommerce: buildEcommerceObject({
                        currency,
                        value: orderValue,
                        coupon: couponValue,
                        tax: computeTaxFromTtc(orderValue),
                        paymentType: paymentMethod,
                        items,
                    }),
                },
                {
                    dedupeKey: createEventDedupeKey(
                        "add_payment_info",
                        giftPromotionId || transactionId || "gift_payment"
                    ),
                }
            );

            pushDataLayerEvent(
                "purchase",
                {
                    user_data: giftUserData,
                    ...buildGtmEventFields({
                        language: i18n.language,
                        user: resolvedTrackingUser || auth?.user,
                    }),
                    ecommerce: buildEcommerceObject({
                        currency,
                        transactionId,
                        value: orderValue,
                        coupon: couponValue,
                        tax: computeTaxFromTtc(orderValue),
                        paymentType: paymentMethod,
                        items,
                    }),
                },
                {
                    dedupeKey: createEventDedupeKey(
                        "purchase",
                        transactionId || giftPromotionId || "gift_purchase"
                    ),
                }
            );

            if (
                pendingGiftTracking &&
                giftPromotionId &&
                String(pendingGiftTracking?.promotionId) === String(giftPromotionId)
            ) {
                sessionStorage.removeItem(PENDING_GIFT_TRACKING_KEY);
            }
        } catch (trackingError) {
            console.error("Failed to push gift confirmation events:", trackingError);
        }
        };

        trackGiftEvents();

        return () => {
            cancelled = true;
        };
    }, [success, isPromotion, promotionId, promotionDetails, auth?.user, i18n.language]);

    const handleCalendarClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCalendarClose = () => {
        setAnchorEl(null);
    };

    const addToCalendar = (calendarType) => {
        console.log({ bookingDetails });

        if (!bookingDetails) return;

        const zurichTimeZone = 'Europe/Zurich';
        const utcStart = new Date(bookingDetails.start_before);
        const startDate = new Date(utcStart.toLocaleString('en-US', { timeZone: zurichTimeZone }));
        const endDate = new Date(startDate.getTime() + bookingDetails.duration * 60000);

        const formattedStart = startDate.toLocaleString('fr-CH');
        const formattedEnd = endDate.toLocaleString('fr-CH');

        const title = bookingDetails.products.map(p => `${p.category?.title || ''} - ${p.name} - ${p.duration} min`).join(' + ');
        const therapistNames = (bookingDetails.therapists || [])
            .map(getUserDisplayName)
            .filter(Boolean)
            .join(', ');
        const description = `Rendez-vous avec ${therapistNames}\n\n` +
            `Date: ${formattedStart}\n` +
            `Durée: ${bookingDetails.duration} minutes`;

        const location = [
            `${bookingDetails.address.street_number ? bookingDetails.address.street_number + ' ' : ''}${bookingDetails.address.street}`,
            `${bookingDetails.address.zip_code} ${bookingDetails.address.state}`,
            bookingDetails.address.country
        ].filter(Boolean).join(', ') || "Adresse non spécifiée";


        const formatDate = (date) => {
            return date.toISOString().replace(/-|:|\.\d\d\d/g, '');
        };

        switch (calendarType) {
            case 'google':
                const googleUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;
                window.open(googleUrl, '_blank');
                break;

            case 'apple':
                const icsContent = [
                    'BEGIN:VCALENDAR',
                    'VERSION:2.0',
                    'BEGIN:VEVENT',
                    `DTSTART:${formatDate(startDate)}`,
                    `DTEND:${formatDate(endDate)}`,
                    `SUMMARY:${title}`,
                    `DESCRIPTION:${description}`,
                    `LOCATION:${location}`,
                    'END:VEVENT',
                    'END:VCALENDAR'
                ].join('\n');

                const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Rendez-vous ${title}.ics`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                break;

            case 'outlook':
                const outlookUrl = `https://outlook.office.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdate=${formatDate(startDate)}&enddate=${formatDate(endDate)}&body=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;
                window.open(outlookUrl, '_blank');
                break;

            default:
                break;
        }

        handleCalendarClose();
    };

    return (
        <>
            <MenuHeader />
            {success === true && (
                <div className="content-wrap">
                    <h3>{t('orderConfirmed')}</h3>
                    <div className="list-item-with-image">
                        <div className="title">
                            <img
                                className="https-app"
                                alt="Https app"
                                src={Valide}
                            />
                            <div className="text-wrapper">{t('thankYou')}</div>
                            <p className="div">
                                {t('emailConfirmationMessage')}
                            </p>
                        </div>
                    </div>

                    <p className="merci-de-votre text-center">
                        <span className="span">
                            {t('thankYouForTrust')}
                            <br />
                        </span>
                        <span className="text-wrapper-2">{t('fly')}</span>
                    </p>

                    {isBooking && (
                        <Container sx={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', padding: '0 !important', width: 'auto' }}>
                            <ThemeProvider theme={theme}>
                                <Button variant="contained" color='dark_yellow' onClick={() => navigate('/commande')} sx={{ fontSize: '18px', textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px' }}>
                                    {t('viewOrders')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color='white_dark'
                                    onClick={handleCalendarClick}
                                    sx={{ fontSize: '18px', textTransform: 'none', border: '1px solid #333', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px', gap: '5px' }}
                                >
                                    {t('addToCalendar')}
                                </Button>

                                <StyledMenu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleCalendarClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center',
                                    }}
                                >
                                    <Paper elevation={0} sx={{ padding: '8px' }}>
                                        <Typography variant="subtitle1" sx={{ padding: '8px 16px', color: 'text.secondary' }}>
                                            {t('chooseCalendar')}
                                        </Typography>
                                        <StyledMenuItem onClick={() => addToCalendar('google')}>
                                            <ListItemIcon>
                                                <Google fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <Typography variant="body1">{t('googleCalendar')}</Typography>
                                        </StyledMenuItem>
                                        <StyledMenuItem onClick={() => addToCalendar('apple')}>
                                            <ListItemIcon>
                                                <Apple fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <Typography variant="body1">{t('appleCalendar')}</Typography>
                                        </StyledMenuItem>
                                        <StyledMenuItem onClick={() => addToCalendar('outlook')}>
                                            <ListItemIcon>
                                                <Microsoft fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <Typography variant="body1">{t('outlookCalendar')}</Typography>
                                        </StyledMenuItem>
                                    </Paper>
                                </StyledMenu>
                            </ThemeProvider>
                        </Container>
                    )}
                </div>
            )}

            {success === false && (
                <div className="content-wrap">
                    <div className="list-item-with-image">
                        <div className="title">
                            <img
                                className="https-app"
                                alt="Https app"
                                src='assets/images/icon-failed.svg'
                            />
                            <div className="text-wrapper">{t('oops')}</div>
                            <p className="div">
                                {t('paymentFailed')}
                            </p>
                            <p className="div">
                                {t('paymentFailed_two')}
                            </p>
                        </div>
                    </div>

                    <p className="merci-de-votre text-center">
                        <span className="span">
                            {t('thankYouForTrust')}
                            <br />
                        </span>
                        <span className="text-wrapper-2">{t('fly')}</span>
                    </p>

                    <Container sx={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', padding: '0 !important', width: 'auto' }}>
                        <ThemeProvider theme={theme}>
                            <Button variant="contained" color='dark_yellow' onClick={() => navigate(-1)} sx={{ fontSize: '18px', textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px' }}>
                                {t('back')}
                            </Button>
                        </ThemeProvider>
                    </Container>
                </div>
            )}
        </>
    );
};

export default ConfirmationPage;
